import { richTextToPlain } from "../common/rich-text/richText";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setMessages,
  setWithMessagesConversations,
  setWithMessagesGroups,
  updateMessage,
  markMessagesAsRead as markMessagesAsReadAction,
  promoteConversation,
  clearConversationUnread,
} from "../Slices/ChatSlice";
import { callApi } from "../common/Apicall";
import groupProfile from "../../assets/Images/group.png";
import userProfile from "../../assets/Images/user.png";
import toast from "react-hot-toast";
import { addNotification } from "../Slices/NotificationsSlice";

const useListenMessages = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state?.data?.userInfo?.data);
  const isLimitedRightsUser = data?.UserId == 1204;
  const connection = useSelector((state) => state.socket.socketConnection);
  const messages = useSelector((state) => state.conversation.messages);
  const isChatModalOpen = useSelector(
    (state) => state.conversation.isChatModalOpen,
  );
  const URL = process.env.REACT_APP_URL;
  const currentUserId = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId,
  );
  const selectedUser = useSelector(
    (state) => state.conversation.selectedConversation?.value,
  );
  const selectedConversationType = useSelector((state) => state.conversation.conversationType);
  const conversationsList = useSelector(
    (state) => state.conversation.withMessagesConversations || [],
  );
  const groupsList = useSelector(
    (state) => state.conversation.withMessagesGroups || [],
  );

  // Built once and reused. Previously this ran on every render of the hook,
  // allocating a fresh Audio element (and re-downloading the clip) each time.
  const soundRef = useRef(null);
  if (soundRef.current === null) {
    soundRef.current = new Audio("/sounds/notification.mp3");
  }
  const sound = soundRef.current;

  // Holds the newest state/handlers for the SignalR listeners (assigned on
  // every render, just below the handler definitions).
  const latestRef = useRef({});

  // Check if sound is muted from localStorage
  const isSoundMuted = () => {
    const savedMuteState = localStorage.getItem("chatSoundMuted");
    return savedMuteState ? JSON.parse(savedMuteState) : false;
  };

  // Fetch conversations with messages
  const fetchConversations = async () => {
    try {
      const res = await callApi(`${URL}/Chats/users-with-messages`, "GET");
      if (res.error) throw new Error(res.error);
      dispatch(setWithMessagesConversations(res));
    } catch (error) {
      console.error(`Failed to fetch conversations: ${error.message}`);
    }
  };

  // Fetch group conversations with messages
  const fetchGroupConversations = async () => {
    try {
      const res = await callApi(
        `${URL}/GroupChat/Users-With-GroupMessages`,
        "GET",
      );
      if (res.error) throw new Error(res.error);
      dispatch(setWithMessagesGroups(res));
    } catch (error) {
      console.error(`Failed to fetch group conversations: ${error.message}`);
    }
  };

  // Handle new message
  const handleNewMessage = async (message) => {
    const {
      profilePic,
      fullName,
      messageType,
      groupParticipants,
      groupName,
      ticketType,
      ...requiredMessage
    } = message;

    const newMessage = { ...requiredMessage, shouldShake: true };

    // Show toast for new message
    const isSimpleMessageForUser =
      String(currentUserId) === String(newMessage.receiverId) && messageType === "simple";

    const isGroupMessageForUser =
      messageType === "group" &&
      groupParticipants?.some(
        (participant) => String(participant.userId) === String(currentUserId),
      ) &&
      String(currentUserId) !== String(newMessage.senderId);

    if (isSimpleMessageForUser || isGroupMessageForUser) {
      // Check if sound is muted before playing
      if (!isSoundMuted()) {
        void playNotificationSound();
      }

      showToast(
        messageType,
        groupName,
        fullName,
        profilePic,
        requiredMessage,
        ticketType,
      );
    }

    // Handle sound playback and mark messages as read
    const isSimpleMessageInSelectedChat =
      String(selectedUser) === String(newMessage.senderId) &&
      String(currentUserId) === String(newMessage.receiverId) &&
      selectedConversationType === "simple" &&
      messageType === "simple";

    const isGroupMessageInSelectedChat =
      String(selectedUser) === String(newMessage.conversationId) &&
      String(currentUserId) !== String(newMessage.senderId) &&
      selectedConversationType === "group" &&
      messageType === "group";

    let isReadImmediately = false;

    if (isSimpleMessageInSelectedChat || isGroupMessageInSelectedChat) {
      dispatch(setMessages([...latestRef.current.messages, newMessage]));

      if (isChatModalOpen) {
        isReadImmediately = await markMessagesAsRead(
          messageType,
          selectedUser,
          currentUserId,
          connection,
        );
      }
    }

    // ✅ Move the conversation to the top of the sidebar and bump its unread
    // badge locally. This used to refetch the entire conversation list on
    // every single incoming message. A conversation that isn't in the list
    // yet (first ever message from someone) genuinely needs the refetch,
    // since there is no local row to update — that path is kept.
    if (isSimpleMessageForUser) {
      const isKnown = latestRef.current.conversationsList.some(
        (conversation) =>
          conversation?.value != null &&
          String(conversation.value) === String(newMessage.senderId),
      );

      if (isKnown) {
        dispatch(
          promoteConversation({
            conversationId: newMessage.senderId,
            conversationType: "simple",
            // Already-read messages must not add to the badge.
            incrementUnread: !isReadImmediately,
          }),
        );
      } else {
        await fetchConversations();
      }
    }

    if (isGroupMessageForUser) {
      const isKnown = latestRef.current.groupsList.some(
        (conversation) =>
          conversation?.value != null &&
          String(conversation.value) === String(newMessage.conversationId),
      );

      if (isKnown) {
        dispatch(
          promoteConversation({
            conversationId: newMessage.conversationId,
            conversationType: "group",
            incrementUnread: !isReadImmediately,
          }),
        );
      } else {
        await fetchGroupConversations();
      }
    }
  };

  // Show custom toast
  const showToast = (
    messageType,
    groupName,
    fullName,
    profilePic,
    requiredMessage,
    ticketType,
  ) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-[300px] w-full bg-gray-50 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-gray-200 ring-opacity-50 mr-16`}
        >
          <div className="flex-1 w-0 p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <img
                  className="h-8 w-8 rounded-full"
                  src={
                    messageType === "group"
                      ? groupProfile
                      : profilePic
                        ? `data:image/png;base64,${profilePic}`
                        : userProfile
                  }
                  alt=""
                />
              </div>
              <div className="ml-2 flex-1 overflow-hidden">
                {messageType === "group" && groupName && (
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {groupName || "Unknown Group"}
                  </p>
                )}
                <p
                  className={`text-xs ${
                    messageType === "simple" ? "font-medium" : "font-normal"
                  } text-gray-900 truncate`}
                >
                  {fullName || "Unknown User"}
                </p>
                <p className="mt-1 text-xs text-gray-600 truncate">
                  {requiredMessage.content ||
                    requiredMessage.fileName ||
                    "New message"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.remove(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-2 flex items-center justify-center text-xs font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      ),
      {
        position: "bottom-right",
        // duration: ticketType ? Infinity : 5000,
        duration: 5000,
      },
    );
  };
  // Show notification toast
  const showNotificationToast = (notification) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 mt-16`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="ml-3 flex-1">
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                  {richTextToPlain(notification.message || "")}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.remove(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      ),
      {
        position: "top-right",
        duration: 5000,
      },
    );
  };

  // Play notification sound
  const playNotificationSound = async () => {
    try {
      // Check if sound is muted before playing
      if (isSoundMuted()) {
        return; // Don't play sound if muted
      }

      sound.pause();
      sound.currentTime = 0;
      await sound.play();
    } catch (error) {
      console.warn("Sound playback failed:", error.message);
    }
  };

  // Mark messages as read
  const markMessagesAsRead = async (
    messageType,
    selectedUser,
    currentUserId,
    connection,
  ) => {
    try {
      const endpoint =
        messageType === "simple"
          ? "Chats/MarkAllMessagesAsRead"
          : "GroupChat/MarkMessagesAsRead";
      const payload =
        messageType === "simple"
          ? { senderId: selectedUser, receiverId: currentUserId }
          : { groupId: selectedUser, userId: currentUserId };

      const result = await callApi(`${URL}/${endpoint}`, "POST", payload);
      if (result?.error) throw new Error(result.error);

      // Update Redux store to mark messages as read
      dispatch(
        markMessagesAsReadAction({
          readerId: currentUserId,
          targetUserId: selectedUser,
          conversationType: messageType,
        }),
      );

      // Clear just this conversation's badge instead of refetching every
      // conversation — marking one thread read cannot change any other row.
      dispatch(
        clearConversationUnread({
          conversationId: selectedUser,
          conversationType: messageType === "simple" ? "simple" : "group",
        }),
      );
      // Notify other clients via SignalR
      if (connection?.state === "Connected") {
        const targetUserId = String(selectedUser);
        const readerId = String(currentUserId);
        try {
          await connection.invoke("ReadMessages", targetUserId, readerId);
        } catch (receiptError) {
          console.error("Read receipt delivery failed:", receiptError);
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to mark messages as read:", error.message);
      toast.error(`Failed to mark messages as read: ${error.message}`);
      return false;
    }
  };

  // Handle updated message
  const handleUpdatedMessage = async (targetUserId, updatedMessage) => {
    const { messageId, senderId, ...updatedProperties } = updatedMessage;

    if (
      (selectedUser == senderId && currentUserId == targetUserId) ||
      (selectedUser == targetUserId && currentUserId != senderId)
    ) {
      dispatch(updateMessage({ messageId, updatedProperties }));
    }
  };

  // Handle received notification
  const handleReceiveNotification = async (notification) => {
    if (notification.userId == currentUserId && !isLimitedRightsUser) {
      dispatch(addNotification(notification));

      // Show toast notification at top right
      showNotificationToast(notification);
    }
  };

  // Always-current values and handlers. The SignalR handlers below are
  // registered once per connection and read through this ref, so they never
  // close over stale state without the effect having to depend on it.
  latestRef.current = {
    messages,
    conversationsList,
    groupsList,
    handleNewMessage,
    handleUpdatedMessage,
    handleReceiveNotification,
  };

  useEffect(() => {
    if (!connection) {
      console.warn("Socket connection is not available.");
      return;
    }

    // Stable wrappers that delegate to the latest handler. Previously the
    // handlers themselves were subscribed with `messages` in the dependency
    // array, so all three listeners were torn down and re-registered on every
    // single message — now they are attached once per connection.
    const onReceiveMessage = (...args) =>
      latestRef.current.handleNewMessage(...args);
    const onReceiveUpdatedMessage = (...args) =>
      latestRef.current.handleUpdatedMessage(...args);
    const onReceiveNotification = (...args) =>
      latestRef.current.handleReceiveNotification(...args);

    connection.on("ReceiveMessage", onReceiveMessage);
    connection.on("ReceiveUpdatedMessage", onReceiveUpdatedMessage);
    connection.on("ReceiveNotification", onReceiveNotification);

    return () => {
      connection.off("ReceiveMessage", onReceiveMessage);
      connection.off("ReceiveUpdatedMessage", onReceiveUpdatedMessage);
      connection.off("ReceiveNotification", onReceiveNotification);
    };
  }, [connection]);

  return null;
};

export default useListenMessages;
